// Reusable runtime guards and accessors for Roles feature
import { safeJSONParse } from 'utils/data/parse.utils';
import type { Member, PermItem } from './Roles.types';

export const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export const toNumber = (v: unknown, def?: number): number | undefined => {
  if (isNumber(v)) return v as number;
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
  return def;
};

// Accept any unknown input — callers may pass typed objects (e.g., `PermItem`).
// We perform runtime checks before accessing properties.
type MaybeItem = unknown | PermItem | Member;

export const getIndex = (item?: MaybeItem): number | undefined => {
  if (!item || typeof item !== 'object') return undefined;
  return toNumber((item as PermItem).index);
};

export const getLinked = (item?: MaybeItem): number => {
  if (!item || typeof item !== 'object') return 0;
  return toNumber((item as PermItem).linked, 0) ?? 0;
};

export const getEnableLinked = (item?: MaybeItem): number => {
  if (!item || typeof item !== 'object') return 0;
  return toNumber((item as PermItem).enableLinked, 0) ?? 0;
};

export const getMemberUserId = (m?: MaybeItem): number | undefined => {
  if (!m || typeof m !== 'object') return undefined;
  return toNumber((m as Member).userId);
};

export const toNumberArray = (arr: unknown[] = []): number[] =>
  arr.map((v) => toNumber(v)).filter((n): n is number => n !== undefined);

export const isBrowser = (): boolean => typeof window !== 'undefined';

export const readLocalJSON = <T>(key: string, fallback: T): T => {
  if (!isBrowser()) return fallback;
  return safeJSONParse(localStorage.getItem(key), fallback);
};

export const writeLocalJSON = <T>(key: string, value: T | null): void => {
  if (!isBrowser()) return;
  if (value === null) {
    localStorage.removeItem(key);
    return;
  }
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // surface serialization/quota errors for debugging
    // eslint-disable-next-line no-console
    console.log('writeLocalJSON error', e);
  }
};

export const getAnyMemberId = (m?: MaybeItem): number | undefined => {
  if (!m || typeof m !== 'object') return undefined;
  const obj = m as Member;
  const candidates = [obj.userId, obj.uid, obj.user_id, obj.id];
  for (const c of candidates) {
    const n = toNumber(c);
    if (n !== undefined) return n;
  }
  return undefined;
};

export default {
  isNumber,
  toNumber,
  getIndex,
  getLinked,
  getEnableLinked,
  getMemberUserId,
  toNumberArray,
  isBrowser,
  readLocalJSON,
  writeLocalJSON,
  getAnyMemberId,
};
